/**
 * How a piece of media renders, derived from its MIME type. Drives the note
 * attachment branch: thumbnail tile (image/pdf/video) vs. file card (doc).
 *
 * This module is the client's single source of truth for the media-kind
 * taxonomy: the MIME→extension map, the per-class byte caps, and the kind/icon
 * lookups all live here so the picker, the upload pre-check, the download
 * namer, and the file card stay in agreement. The maps MIRROR the server's
 * accepted-type maps in apps/api/core/src/media/media.service.ts (separate
 * package, can't be imported) — keep the two in sync when adding a type.
 */
import type { IconName } from "@salestrak/ui";

export type MediaKind = "image" | "pdf" | "video" | "file";

export function mediaKind(contentType: string): MediaKind {
  if (contentType.startsWith("image/")) return "image";
  if (contentType.startsWith("video/")) return "video";
  if (contentType === "application/pdf") return "pdf";
  return "file";
}

/** Whether this media has a rendered thumbnail (image/pdf/video do; documents
 * fall back to a file icon). The single test for "should a tile fetch a thumb." */
export function hasThumbnail(contentType: string): boolean {
  return mediaKind(contentType) !== "file";
}

// MIME → file extension. The authoritative map for download filenames and the
// file-card type label. Mirrors the server's CONTENT_TYPE_EXTENSIONS.
const CONTENT_TYPE_EXTENSIONS: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
  "image/heic": "heic",
  "image/heif": "heif",
  "video/mp4": "mp4",
  "video/webm": "webm",
  "video/quicktime": "mov",
  "application/pdf": "pdf",
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "docx",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.ms-powerpoint": "ppt",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "pptx",
  "text/csv": "csv",
  "text/plain": "txt",
};

/** File extension (no dot) for a content type, or null if unknown. */
export function extensionForContentType(contentType: string): string | null {
  return CONTENT_TYPE_EXTENSIONS[contentType] ?? null;
}

/** `accept` attribute for the note-attachment file picker, derived from the
 * canonical type map so it can't drift from what we actually accept: every
 * non-image type listed verbatim, the image types collapsed to `image/*`. */
export const NOTE_MEDIA_ACCEPT = [
  "image/*",
  ...Object.keys(CONTENT_TYPE_EXTENSIONS).filter(
    (type) => !type.startsWith("image/"),
  ),
].join(",");

// Upload byte caps by media class — MUST match the server's per-class caps
// (MAX_IMAGE_BYTES / MAX_DOCUMENT_BYTES / MAX_VIDEO_BYTES in media.service.ts).
// The client pre-validates against these so an oversize file is rejected up
// front with a clear message instead of after a full process + upload attempt.
export const MAX_IMAGE_BYTES = 20 * 1024 * 1024;
export const MAX_DOCUMENT_BYTES = 25 * 1024 * 1024;
export const MAX_VIDEO_BYTES = 200 * 1024 * 1024;

/** Hard upload size ceiling for a content type, by media class. */
export function maxBytesForContentType(contentType: string): number {
  const kind = mediaKind(contentType);
  if (kind === "video") return MAX_VIDEO_BYTES;
  if (kind === "image") return MAX_IMAGE_BYTES;
  return MAX_DOCUMENT_BYTES; // pdf + office docs
}

// Icon (@salestrak/ui icon name) for a document/file card by content type.
// Restricted to icons present in the library's registry.
const FILE_ICONS: Record<string, IconName> = {
  "application/pdf": "file-text",
  "text/csv": "table",
  "application/vnd.ms-excel": "table",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "table",
  "application/msword": "file-text",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "file-text",
  "text/plain": "file-text",
};

export function fileIconName(contentType: string): IconName {
  return FILE_ICONS[contentType] ?? "file";
}

// Short uppercase label shown on a file card (e.g. "PDF", "DOCX"), derived from
// the filename extension, then the content type's mapped extension, then the
// MIME subtype as a last resort.
export function fileTypeLabel(
  contentType: string,
  fileName?: string | null,
): string {
  const ext =
    (fileName?.includes(".") ? fileName.split(".").pop() : null) ??
    CONTENT_TYPE_EXTENSIONS[contentType] ??
    contentType.split("/").pop();
  return (ext ?? "file").toUpperCase().slice(0, 5);
}
